import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Plus, Trash2, X, Upload, Sparkles, Link as LinkIcon, FileText, Layers, Users, ChevronRight } from 'lucide-react';
import {
  adminKbTargets, adminKbDomains, adminKbAssignAgentDomains, adminKbUsage, adminKbIngest, adminKbIngestDoc,
  adminKbRemoveSource, adminKbClear, type KbScope, type KbUsageRow, type KbTargetAgent, type KbDomainRow,
} from '../../services/adminService';
import { readFileAsText, readFileAsBase64, isTextFile, isDocFile, KB_UPLOAD_EXTS } from '../../utils/readTextFile';
import { formatBytes } from '../../utils/chatAttachments';
import { useConfirm } from '../../hooks/useConfirm';

const input = 'w-full rounded-lg border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-3 py-2 text-sm focus:outline-none focus:border-secondary-500';
const GLOBAL_ID = '__all__';

/** Ingest + source management for one KB scope (a domain module, or the global all-agents scope). */
const ScopeManager: React.FC<{
  scope: KbScope; id?: string; usage: KbUsageRow | null; tiers: string[];
  onChanged: (u: KbUsageRow) => void; onError: (m: string) => void;
}> = ({ scope, id, usage, tiers, onChanged, onError }) => {
  const [model, setModel] = useState('');
  const [kind, setKind] = useState<'text' | 'html' | 'url'>('text');
  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [confirm, confirmDialog] = useConfirm();

  const sources = usage?.sources || [];
  const pct = usage && usage.cap ? Math.min(100, Math.round((usage.bytes / usage.cap) * 100)) : 0;
  const full = !!usage && usage.remaining <= 0;

  const run = async (fn: () => Promise<KbUsageRow>) => {
    setBusy(true); onError(''); // clear any prior error on a fresh action
    try { onChanged(await fn()); } catch (e: any) { onError(e?.message || 'Something went wrong'); } finally { setBusy(false); }
  };
  const ingest = () => { if (content.trim()) run(() => adminKbIngest(scope, id, kind, content.trim(), model || undefined)).then(() => setContent('')); };
  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      let last = usage;
      for (const f of Array.from(files)) {
        if (isTextFile(f.name, f.type)) { const t = await readFileAsText(f); if (t.trim()) last = await adminKbIngest(scope, id, 'text', t, model || undefined, f.name); }
        else if (isDocFile(f.name, f.type)) { const b = await readFileAsBase64(f); last = await adminKbIngestDoc(scope, id, b, f.type || 'application/octet-stream', f.name, model || undefined); }
        else onError(`"${f.name}" isn't a supported file.`);
      }
      if (last) onChanged(last);
    } catch (e: any) { onError(e?.message || 'Could not read that file'); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  return (
    <div className="space-y-3">
      {usage && (
        <div>
          <div className="flex items-center justify-between text-[11px] text-secondary-500 dark:text-secondary-400 mb-1">
            <span>{formatBytes(usage.bytes)} of {formatBytes(usage.cap)} used</span>
            {sources.length > 0 && <button onClick={async () => { if (await confirm({ title: 'Clear knowledge base?', description: `Clear all ${sources.length} source${sources.length === 1 ? '' : 's'} from this knowledge base? This permanently deletes them and cannot be undone.`, confirmText: 'Clear all', variant: 'destructive' })) run(() => adminKbClear(scope, id)); }} disabled={busy} className="inline-flex items-center gap-1 hover:text-red-500"><Trash2 className="h-3 w-3" /> Clear all</button>}
          </div>
          <div className="h-1.5 w-full rounded-full bg-secondary-200 dark:bg-secondary-700 overflow-hidden">
            <div className={`h-full ${full ? 'bg-red-500' : 'bg-secondary-800 dark:bg-secondary-200'}`} style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}
      {sources.length > 0 && (
        <div className="flex flex-col gap-1">
          {sources.map((s) => (
            <div key={s.source} className="inline-flex items-center gap-1.5 rounded-md bg-secondary-100 dark:bg-secondary-800 px-2 py-1 text-xs text-secondary-600 dark:text-secondary-300">
              {/^https?:/.test(s.source) ? <LinkIcon className="h-3 w-3 shrink-0" /> : <FileText className="h-3 w-3 shrink-0" />}
              <span className="truncate flex-1" title={s.source}>{s.source}</span>
              <span className="shrink-0 text-[10px] text-secondary-400">{formatBytes(s.bytes)}</span>
              <button onClick={() => run(() => adminKbRemoveSource(scope, id, s.source))} disabled={busy} aria-label={`Remove ${s.source}`} className="shrink-0 text-secondary-400 hover:text-red-500"><X className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      )}
      <label className="block text-xs text-secondary-500">
        Distil with model (optional)
        <select value={model} onChange={(e) => setModel(e.target.value)} className={input}>
          <option value="">Store raw (no model)</option>
          {tiers.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </label>
      <div className="flex gap-2">
        <select value={kind} onChange={(e) => setKind(e.target.value as 'text' | 'html' | 'url')} className={`${input} w-24 shrink-0`}>
          <option value="text">Text</option><option value="html">HTML</option><option value="url">URL</option>
        </select>
        <input value={content} onChange={(e) => setContent(e.target.value)} disabled={full}
          onKeyDown={(e) => { if (e.key === 'Enter' && kind === 'url') ingest(); }}
          placeholder={kind === 'url' ? 'https://…' : kind === 'html' ? 'Paste HTML' : 'Paste reference text'} className={input} />
        <button onClick={ingest} disabled={busy || !content.trim() || full} className="shrink-0 rounded-lg bg-secondary-900 dark:bg-white px-3 py-2 text-sm font-medium text-white dark:text-secondary-900 disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
        </button>
      </div>
      {kind === 'text' && content.length > 200 && <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={5} className={input} />}
      <input ref={fileRef} type="file" multiple accept={KB_UPLOAD_EXTS.join(',')} className="hidden" onChange={(e) => upload(e.target.files)} />
      <button onClick={() => fileRef.current?.click()} disabled={busy || full} className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-dashed border-secondary-300 dark:border-secondary-700 px-3 py-2.5 text-xs text-secondary-500 hover:border-secondary-500 disabled:opacity-40">
        <Upload className="h-3.5 w-3.5" /> Upload documents (.txt, .md, .csv, .json, .pdf, .docx)
      </button>
      {model && <p className="inline-flex items-center gap-1 text-[11px] text-secondary-400"><Sparkles className="h-3 w-3" /> Distilled with {model} before storing.</p>}
      {confirmDialog}
    </div>
  );
};

/** Assign each agent's specialised KB domains (mapping into the domain layer). */
const AgentMapping: React.FC<{ agents: KbTargetAgent[]; domains: string[]; onSaved: () => void; onError: (m: string) => void }>
  = ({ agents, domains, onSaved, onError }) => {
  const [openId, setOpenId] = useState<string | null>(null);
  const [sel, setSel] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const open = (a: KbTargetAgent) => { setOpenId(a.id); setSel(a.knowledge_domains || []); };
  const toggle = (d: string) => setSel((s) => (s.includes(d) ? s.filter((x) => x !== d) : [...s, d]));
  const save = async (agentId: string) => {
    setBusy(true);
    try { await adminKbAssignAgentDomains(agentId, sel); setOpenId(null); onSaved(); }
    catch (e: any) { onError(e?.message || 'Could not save'); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5">
      <p className="text-xs text-secondary-500 dark:text-secondary-400">Assign the KB domain modules each agent specialises in. Agents sharing a domain read the same module.</p>
      {agents.map((a) => (
        <div key={a.id} className="rounded-lg border border-secondary-200 dark:border-secondary-700">
          <button onClick={() => (openId === a.id ? setOpenId(null) : open(a))} className="w-full flex items-center justify-between px-3 py-2 text-sm">
            <span className="font-medium text-secondary-900 dark:text-white">{a.name}</span>
            <span className="flex items-center gap-2 text-xs text-secondary-400">
              {(a.knowledge_domains || []).join(', ') || 'no domains'}
              <ChevronRight className={`h-4 w-4 transition-transform ${openId === a.id ? 'rotate-90' : ''}`} />
            </span>
          </button>
          {openId === a.id && (
            <div className="px-3 pb-3 space-y-2">
              <div className="flex flex-wrap gap-1.5">
                {domains.map((d) => (
                  <button key={d} onClick={() => toggle(d)} className={`rounded-full px-2.5 py-1 text-xs border ${sel.includes(d) ? 'bg-secondary-900 text-white border-secondary-900 dark:bg-white dark:text-secondary-900 dark:border-white' : 'border-secondary-300 dark:border-secondary-600 text-secondary-600 dark:text-secondary-300'}`}>{d}</button>
                ))}
              </div>
              <button onClick={() => save(a.id)} disabled={busy} className="rounded-lg bg-secondary-900 dark:bg-white px-3 py-1.5 text-xs font-medium text-white dark:text-secondary-900 disabled:opacity-40">
                {busy ? 'Saving…' : 'Save mapping'}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

/**
 * Admin knowledge-base LAYER manager. The KB is a set of DOMAIN MODULES (not per-agent silos): manage each
 * module's sources + iterations centrally, see which agents map to it (shared/overlapping), inject into ALL
 * agents (global), and assign each agent's specialised domains. Optional model distillation before storing.
 */
const KnowledgeAdminPanel: React.FC = () => {
  const [view, setView] = useState<'domains' | 'agents'>('domains');
  const [domains, setDomains] = useState<KbDomainRow[]>([]);
  const [agents, setAgents] = useState<KbTargetAgent[]>([]);
  const [taxonomy, setTaxonomy] = useState<string[]>([]);
  const [tiers, setTiers] = useState<string[]>([]);
  const [selected, setSelected] = useState<string>(GLOBAL_ID); // GLOBAL_ID = all-agents scope
  const [globalUsage, setGlobalUsage] = useState<KbUsageRow | null>(null);
  const [error, setError] = useState('');

  const loadDomains = useCallback(() => { adminKbDomains().then(setDomains).catch((e) => setError(e.message)); }, []);
  const loadTargets = useCallback(() => {
    adminKbTargets().then((r) => { setAgents(r.agents); setTaxonomy(r.domains); setTiers(r.tiers); }).catch((e) => setError(e.message));
  }, []);
  useEffect(() => { loadDomains(); loadTargets(); adminKbUsage('all').then(setGlobalUsage).catch(() => {}); }, [loadDomains, loadTargets]);

  const selDomain = domains.find((d) => d.id === selected) || null;
  const scope: KbScope = selected === GLOBAL_ID ? 'all' : 'domain';
  const scopeId = selected === GLOBAL_ID ? undefined : selected;
  const usage = selected === GLOBAL_ID ? globalUsage : selDomain?.usage || null;

  const onChanged = (u: KbUsageRow) => {
    if (selected === GLOBAL_ID) setGlobalUsage(u);
    else setDomains((ds) => ds.map((d) => (d.id === selected ? { ...d, usage: u } : d)));
    // Refresh mapped-agent counts + other modules. If the selected module vanished (e.g. emptied + dropped
    // server-side), fall back to the global scope so the manager never renders a blank/null usage.
    adminKbDomains().then((next) => {
      setDomains(next);
      if (selected !== GLOBAL_ID && !next.some((d) => d.id === selected)) setSelected(GLOBAL_ID);
    }).catch((e) => setError(e.message));
  };

  return (
    <div className="max-w-4xl space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">Knowledge base</h2>
        <p className="text-sm text-secondary-500 dark:text-secondary-400">A layer of domain modules mapped to agents — manage each module&apos;s knowledge and iterations here; agents that share a domain read the same module.</p>
      </div>

      <div className="flex gap-2">
        {(['domains', 'agents'] as const).map((v) => (
          <button key={v} onClick={() => setView(v)} className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium ${view === v ? 'bg-secondary-900 text-white dark:bg-white dark:text-secondary-900' : 'text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-800'}`}>
            {v === 'domains' ? <Layers className="h-4 w-4" /> : <Users className="h-4 w-4" />}{v === 'domains' ? 'Domain modules' : 'Agent mapping'}
          </button>
        ))}
      </div>

      {error && <p className="text-xs text-red-500">{error}</p>}

      {view === 'domains' ? (
        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4">
          {/* Module list */}
          <div className="space-y-1">
            <button onClick={() => setSelected(GLOBAL_ID)} className={`w-full text-left rounded-lg px-3 py-2 text-sm ${selected === GLOBAL_ID ? 'bg-secondary-100 dark:bg-secondary-800' : 'hover:bg-secondary-50 dark:hover:bg-secondary-800/50'}`}>
              <div className="font-medium text-secondary-900 dark:text-white">All agents (global)</div>
              <div className="text-[11px] text-secondary-400">{globalUsage ? formatBytes(globalUsage.bytes) : '—'} · every agent</div>
            </button>
            {domains.map((d) => (
              <button key={d.id} onClick={() => setSelected(d.id)} className={`w-full text-left rounded-lg px-3 py-2 text-sm ${selected === d.id ? 'bg-secondary-100 dark:bg-secondary-800' : 'hover:bg-secondary-50 dark:hover:bg-secondary-800/50'}`}>
                <div className="font-medium text-secondary-900 dark:text-white">{d.id}</div>
                <div className="text-[11px] text-secondary-400">{formatBytes(d.usage.bytes)} · {d.agents.length} agent{d.agents.length === 1 ? '' : 's'}</div>
              </button>
            ))}
          </div>
          {/* Selected module manager */}
          <div className="space-y-3">
            {selDomain && (
              <div className="flex flex-wrap gap-1 text-[11px] text-secondary-400">
                <span className="mr-1">Mapped agents:</span>
                {selDomain.agents.length ? selDomain.agents.map((a) => <span key={a.id} className="rounded bg-secondary-100 dark:bg-secondary-800 px-1.5 py-0.5">{a.name}</span>) : <span>none yet</span>}
              </div>
            )}
            <ScopeManager scope={scope} id={scopeId} usage={usage} tiers={tiers} onChanged={onChanged} onError={setError} />
          </div>
        </div>
      ) : (
        <AgentMapping agents={agents} domains={taxonomy} onSaved={() => { loadTargets(); loadDomains(); }} onError={setError} />
      )}
    </div>
  );
};

export default KnowledgeAdminPanel;
